/**
 * Hook for handling KessPay 3DS authentication
 */
import { useCallback, useRef } from 'react';
export function useKessPayAction({ setError, setIsLoading, hookOptionsRef, }) {
    const challengeInProgressRef = useRef(false);
    const handleKessPayAuth = useCallback(async (payment, actionData, options = {}) => {
        if (challengeInProgressRef.current) {
            console.log('KessPay 3DS challenge already in progress');
            return;
        }
        const threeDSData = actionData?.metadata?.threeds;
        if (!threeDSData?.challengeHtml) {
            const errorMsg = 'Missing KessPay 3DS challenge HTML';
            console.error(errorMsg);
            setError(errorMsg);
            setIsLoading(false);
            options.onFailure?.(errorMsg);
            options.onPaymentFailed?.({
                code: 'KESSPAY_MISSING_DATA',
                message: errorMsg,
                payment,
            });
            return;
        }
        try {
            challengeInProgressRef.current = true;
            setIsLoading(false); // Stop loading before redirect
            console.log('🔐 [KessPay] Starting 3DS authentication:', {
                sessionId: threeDSData.sessionId,
                orderId: threeDSData.orderId,
                hasHtml: !!threeDSData.challengeHtml,
            });
            // Create a temporary document to execute the challenge HTML
            // This will trigger the auto-submit form and redirect
            const tempDoc = document.implementation.createHTMLDocument('KessPay 3DS Challenge');
            tempDoc.body.innerHTML = threeDSData.challengeHtml;
            // Find and submit the form directly
            const form = tempDoc.querySelector('form');
            if (form) {
                // Create a new form in the current document and submit it
                const redirectForm = document.createElement('form');
                redirectForm.method = form.method || 'POST';
                redirectForm.action = form.action || '';
                redirectForm.style.display = 'none';
                // Copy all form inputs
                const inputs = form.querySelectorAll('input');
                inputs.forEach(input => {
                    const newInput = document.createElement('input');
                    newInput.type = input.type;
                    newInput.name = input.name;
                    newInput.value = input.value;
                    redirectForm.appendChild(newInput);
                });
                // Add form to document and submit
                document.body.appendChild(redirectForm);
                redirectForm.submit();
                console.log('🔐 [KessPay] Redirecting to 3DS challenge...');
            }
            else {
                // Fallback: write the HTML directly to current document
                // This should trigger the auto-submit script
                document.open();
                document.write(threeDSData.challengeHtml);
                document.close();
            }
            // Note: Success callback will be triggered after redirect return
            // Payment status will be updated via webhook
        }
        catch (error) {
            challengeInProgressRef.current = false;
            const errorMsg = error instanceof Error ? error.message : 'Failed to process KessPay 3DS authentication';
            console.error('🔐 [KessPay] 3DS authentication error:', error);
            setError(errorMsg);
            setIsLoading(false);
            options.onFailure?.(errorMsg);
            options.onPaymentFailed?.({
                code: 'KESSPAY_AUTH_FAILED',
                message: errorMsg,
                payment,
            });
            // Hook-level callback (universal handler)
            if (hookOptionsRef.current?.onPaymentFailed) {
                hookOptionsRef.current.onPaymentFailed(errorMsg, {
                    isRedirectReturn: false,
                });
            }
        }
    }, [setError, setIsLoading, hookOptionsRef]);
    return { handleKessPayAuth };
}
